import { Head, Link, usePage } from '@inertiajs/react'
import MainLayout from '../../components/layout/MainLayout'

export default function PolitiqueCookies() {
  const { lastUpdate } = usePage().props

  return (
    <MainLayout>
      <Head title="Politique cookies — SmartImmo" />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <header className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Politique cookies</h1>
          <p className="text-sm text-gray-500">Dernière mise à jour : {lastUpdate}</p>
        </header>

        <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-900 p-4 mb-10 rounded">
          <p className="text-sm leading-relaxed">
            Cette page explique comment SmartImmo (édité par PLUGANDWORK / Sinoia) utilise les
            cookies et technologies similaires sur le site <strong>smartimmo.sinoia.fr</strong>,
            conformément à la directive ePrivacy et aux recommandations de la CNIL.
          </p>
        </div>

        <nav aria-label="Sommaire" className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-12">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Sommaire</h2>
          <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
            <li><a href="#definition" className="hover:text-primary-600">Qu'est-ce qu'un cookie ?</a></li>
            <li><a href="#categories" className="hover:text-primary-600">Cookies utilisés sur SmartImmo</a></li>
            <li><a href="#consentement" className="hover:text-primary-600">Consentement et gestion</a></li>
            <li><a href="#duree" className="hover:text-primary-600">Durée de conservation</a></li>
            <li><a href="#refus" className="hover:text-primary-600">Comment refuser ou supprimer les cookies</a></li>
            <li><a href="#evolutions" className="hover:text-primary-600">Évolution de cette politique</a></li>
          </ol>
        </nav>

        <article className="prose prose-gray max-w-none">
          <section id="definition" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
            <p className="text-gray-700 mb-3">
              Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur,
              tablette, smartphone) lors de la visite d'un site web. Il permet au site de
              reconnaître votre navigateur, de mémoriser certaines informations vous concernant
              et de faciliter votre navigation.
            </p>
            <p className="text-gray-700">
              La présente politique utilise le terme générique « cookies » pour désigner
              également les autres traceurs comparables (local storage, pixels, etc.).
            </p>
          </section>

          <section id="categories" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Cookies utilisés sur SmartImmo</h2>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Cookies strictement nécessaires</h3>
            <p className="text-gray-700 mb-3">
              Ces cookies sont indispensables au fonctionnement du site et à la fourniture du
              service. Ils ne nécessitent pas votre consentement.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border border-gray-200 rounded">
                <thead className="bg-gray-50 text-gray-900">
                  <tr>
                    <th className="px-4 py-3 border-b border-gray-200">Nom</th>
                    <th className="px-4 py-3 border-b border-gray-200">Finalité</th>
                    <th className="px-4 py-3 border-b border-gray-200">Durée</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200 font-mono text-xs">_smartimmo_session</td>
                    <td className="px-4 py-3 border-b border-gray-200">Maintien de la session utilisateur (authentification, panier de navigation)</td>
                    <td className="px-4 py-3 border-b border-gray-200">Session (suppression à la fermeture du navigateur)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200 font-mono text-xs">remember_token</td>
                    <td className="px-4 py-3 border-b border-gray-200">Permettre à un utilisateur authentifié de rester connecté entre deux visites</td>
                    <td className="px-4 py-3 border-b border-gray-200">30 jours</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">CSRF-TOKEN</td>
                    <td className="px-4 py-3">Protection contre les attaques de type Cross-Site Request Forgery</td>
                    <td className="px-4 py-3">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2.2 Cookies de mesure d'audience</h3>
            <p className="text-gray-700">
              SmartImmo n'utilise pas actuellement de cookies de mesure d'audience tiers (Google
              Analytics, etc.). Si cela devait évoluer, votre consentement préalable sera sollicité
              via un bandeau dédié, et la présente politique sera mise à jour.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2.3 Cookies publicitaires et de réseaux sociaux</h3>
            <p className="text-gray-700">
              SmartImmo ne dépose aucun cookie publicitaire ni cookie de partage de réseaux
              sociaux. Aucune donnée n'est transmise à des régies publicitaires ou plateformes
              sociales depuis le site.
            </p>
          </section>

          <section id="consentement" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Consentement et gestion</h2>
            <p className="text-gray-700 mb-3">
              Les cookies strictement nécessaires au fonctionnement du site sont dispensés de
              consentement, conformément à l'article 82 de la loi « Informatique et Libertés ».
            </p>
            <p className="text-gray-700">
              Si SmartImmo venait à intégrer des cookies soumis à consentement (mesure d'audience
              non exemptée, publicité, etc.), un bandeau permettant d'accepter, de refuser ou de
              personnaliser vos choix s'affichera dès votre arrivée sur le site, et vous pourrez
              modifier vos préférences à tout moment depuis un lien dédié.
            </p>
          </section>

          <section id="duree" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Durée de conservation</h2>
            <p className="text-gray-700">
              La durée de conservation maximale des cookies est de 13 mois, conformément aux
              recommandations de la CNIL. Cette durée n'est pas prolongée à chaque visite — un
              renouvellement actif est requis. Voir le tableau au § 2 pour les durées effectives
              de chaque cookie utilisé.
            </p>
          </section>

          <section id="refus" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Comment refuser ou supprimer les cookies</h2>
            <p className="text-gray-700 mb-3">
              Vous pouvez à tout moment configurer votre navigateur pour bloquer ou supprimer les
              cookies. La procédure dépend de votre navigateur :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-4">
              <li><strong>Chrome</strong> : Paramètres → Confidentialité et sécurité → Cookies et autres données des sites</li>
              <li><strong>Firefox</strong> : Paramètres → Vie privée et sécurité → Cookies et données de sites</li>
              <li><strong>Safari</strong> : Préférences → Confidentialité → Cookies et données de site</li>
              <li><strong>Edge</strong> : Paramètres → Cookies et autorisations de site</li>
            </ul>
            <p className="text-gray-700">
              <strong>Attention</strong> : le blocage des cookies strictement nécessaires peut
              empêcher l'utilisation normale du site, notamment l'authentification à votre
              espace agence.
            </p>
          </section>

          <section id="evolutions" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Évolution de cette politique</h2>
            <p className="text-gray-700">
              La présente politique cookies peut être amenée à évoluer, notamment en fonction de
              l'évolution du site et des obligations légales. La date de dernière mise à jour
              indiquée en haut de page permet d'identifier la version applicable. Nous vous
              invitons à la consulter régulièrement.
            </p>
            <p className="text-gray-700 mt-3">
              Pour toute question, consultez la{' '}
              <Link href="/politique-confidentialite" className="text-primary-600 hover:underline">
                politique de confidentialité
              </Link>{' '}
              ou contactez-nous à{' '}
              <a href="mailto:contact@sinoia.fr" className="text-primary-600 hover:underline">
                contact@sinoia.fr
              </a>.
            </p>
          </section>
        </article>
      </div>
    </MainLayout>
  )
}
